/**
 * สร้างลูกค้าใหม่ — `POST /v1/customers` (D-126)
 *
 * **หน้าเต็มของตัวเอง เปิดด้วย `?view=create`** ไม่ใช่ modal อีกต่อไป ผังหน้าเหมือน
 * `CustomerEditView` ทุกอย่าง ต่างกันที่ **รหัสลูกค้ากรอกได้** (เขียนครั้งเดียวตอนนี้
 * เท่านั้น — หลังจากนี้แก้ไม่ได้อีกเลย, D-089) และสถานะเริ่มต้นเป็น `ACTIVE`
 *
 * ที่อยู่และผู้ติดต่อ 1-3 ชุดกรอกได้ตั้งแต่สร้าง (D-126 ข้อ 2) เพื่อไม่ให้ต้องสร้างแล้ว
 * กลับมาแก้ทันที — ส่วนฟอร์มผู้ติดต่อใช้ `CustomerContactFields`/`contactSlots`/
 * `validateContacts`/`buildContactsPayload` ร่วมกับหน้าแก้ไข (D-126 ข้อ 3) ไม่เขียนซ้ำ
 *
 * **บันทึกสำเร็จ → นำทางไปหน้ารายละเอียดของลูกค้าที่เพิ่งสร้าง** พร้อม router state
 * `{ customerCreated: true }` เหมือนที่หน้าแก้ไขทำกับ `customerUpdated` — `CustomerDetailView`
 * อ่าน flag นี้เพื่อแสดงข้อความสำเร็จ และ `onCreated()` ถูกเรียกก่อนนำทางเสมอเพื่อให้
 * รายการ (`CustomerScreen`) โหลดใหม่
 *
 * 409 รหัสซ้ำ (`CUSTOMER_CODE_TAKEN`) แสดงด้วย `CustomerFailureNotice` ที่ช่องรหัส
 * ข้อมูลที่กรอกไว้ไม่หาย — เหมือนพฤติกรรมของกล่องสร้างเดิม
 *
 * ⚠️ `CustomerFailureNotice` ถูก import จาก `CustomerScreen` ซึ่งเป็นวงจร import
 * เดียวกับที่ `CustomerEditView`/`CustomerDetailView` อธิบายไว้แล้ว — ปลอดภัยด้วย
 * เหตุผลเดียวกัน (function declaration ที่ hoist ตอน instantiate)
 */

import { useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import {
  buildContactsPayload,
  contactSlots,
  CustomerContactFields,
  problemText,
  validateContacts,
  type ContactFormState,
  type ContactProblem,
} from './CustomerEditView';
import { CustomerFailureNotice } from './CustomerScreen';
import { InfoHint } from '../components/InfoHint';
import { createCustomer, type CustomerFailure } from '../../lib/customers/client';
import { customerStatusLabel } from '../../lib/customers/customersView';
import {
  ACTIVE_CUSTOMER_STATUS,
  CUSTOMER_CODE_MAX_LENGTH,
  CUSTOMER_NAME_MAX_LENGTH,
  CUSTOMER_STATUSES,
  type CreateCustomerBody,
} from '../../lib/customers/types';

export interface CustomerCreateViewProps {
  /** query ของหน้ารายการที่ปุ่ม "ยกเลิก" ต้องกลับไป — รักษา sort/dir/page/ตัวกรอง */
  readonly cancelSearch: string;
  /** ประกอบ query ของหน้ารายละเอียดจาก id ที่เซิร์ฟเวอร์ตอบมาหลังสร้างสำเร็จ */
  readonly detailSearchFor: (customerId: string) => string;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  /** แจ้ง `CustomerScreen` ให้โหลดทะเบียนใหม่ (D-126) — เรียกก่อนนำทางออกเสมอ */
  readonly onCreated: () => void;
}

interface CreateFormState {
  readonly customer_code: string;
  readonly customer_name: string;
  readonly status: string;
  readonly address: string;
  readonly contacts: readonly ContactFormState[];
}

function initialForm(): CreateFormState {
  return {
    customer_code: '',
    customer_name: '',
    status: ACTIVE_CUSTOMER_STATUS,
    address: '',
    contacts: contactSlots(undefined),
  };
}

type SaveState =
  | { readonly status: 'idle' }
  | { readonly status: 'saving' }
  | { readonly status: 'invalid-form'; readonly problems: readonly string[] }
  | CustomerFailure;

type BuildResult =
  | { readonly ok: true; readonly body: CreateCustomerBody }
  | { readonly ok: false; readonly problems: readonly ContactProblem[] };

/** ประกอบ body ของ `POST` — `address`/`contacts` ส่งเมื่อกรอกเท่านั้น (D-126 ข้อ 2) */
function buildCreateBody(form: CreateFormState): BuildResult {
  const problems = validateContacts(form.contacts);
  if (problems.length > 0) {
    return { ok: false, problems };
  }

  const code = form.customer_code.trim();
  const name = form.customer_name.trim();
  const status = form.status.trim();
  const address = form.address.trim();
  const contacts = buildContactsPayload(form.contacts);

  const body: CreateCustomerBody = {
    customer_code: code,
    customer_name: name,
    ...(status === '' ? {} : { status }),
    ...(address === '' ? {} : { address }),
    ...(contacts.length === 0 ? {} : { contacts }),
  };

  return { ok: true, body };
}

export function CustomerCreateView({
  cancelSearch,
  detailSearchFor,
  apiBaseUrl,
  fetchImpl,
  onCreated,
}: CustomerCreateViewProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [form, setForm] = useState<CreateFormState>(initialForm);
  const [save, setSave] = useState<SaveState>({ status: 'idle' });
  const [contactErrors, setContactErrors] = useState<ReadonlySet<number>>(new Set());

  // ผลของ request ที่กลับมาหลัง component ถูกถอดออกแล้วต้องไม่ถูกเขียนลง state
  const mounted = useRef(true);

  const updateField = (field: 'customer_code' | 'customer_name' | 'status' | 'address', value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateContact = (
    contactNo: number,
    field: Exclude<keyof ContactFormState, 'contact_no'>,
    value: string,
  ) => {
    setForm((current) => ({
      ...current,
      contacts: current.contacts.map((contact) =>
        contact.contact_no === contactNo ? { ...contact, [field]: value } : contact,
      ),
    }));
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (save.status === 'saving') {
      return;
    }

    const built = buildCreateBody(form);
    if (!built.ok) {
      setContactErrors(new Set(built.problems.map((problem) => problem.contact_no)));
      setSave({ status: 'invalid-form', problems: built.problems.map(problemText) });
      return;
    }
    setContactErrors(new Set());

    setSave({ status: 'saving' });
    const result = await createCustomer(built.body, { apiBaseUrl, fetchImpl });
    if (!mounted.current) {
      return;
    }

    if (result.status === 'ready') {
      // ลำดับสำคัญ: แจ้งทะเบียนให้โหลดใหม่ก่อนนำทางออกจากหน้านี้
      onCreated();
      navigate(
        { pathname, search: detailSearchFor(result.customer.customer_id) },
        { state: { customerCreated: true } },
      );
      return;
    }

    // ⚠️ ไม่แตะ `form`: ความล้มเหลว (เช่น 409 รหัสซ้ำ) ไม่ใช่การบันทึก สิ่งที่พิมพ์ไว้ยังอยู่
    setSave(result);
  };

  const saveFailure: CustomerFailure | null =
    save.status === 'idle' || save.status === 'saving' || save.status === 'invalid-form'
      ? null
      : save;

  const codeEmpty = form.customer_code.trim() === '';
  const nameEmpty = form.customer_name.trim() === '';
  const submitDisabled = save.status === 'saving' || codeEmpty || nameEmpty;

  return (
    <div className="cap-page" data-testid="customer-create">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            เพิ่มลูกค้าใหม่
            <InfoHint
              label="เกี่ยวกับหน้าเพิ่มลูกค้าใหม่"
              text="กรอกรหัส ชื่อ ที่อยู่ และผู้ติดต่อได้ตั้งแต่สร้าง"
            />
          </h1>
        </div>
        <div className="cap-page__actions">
          <Link
            className="cap-button cap-button--ghost"
            data-testid="customer-create-cancel"
            to={{ pathname, search: cancelSearch }}
          >
            ยกเลิก
          </Link>
        </div>
      </div>

      {save.status === 'invalid-form' ? (
        <div
          className="cap-banner cap-banner--warning"
          role="status"
          data-testid="customer-create-invalid"
        >
          <strong>ยังบันทึกไม่ได้ — ตรวจผู้ติดต่อก่อน</strong>
          <span>{save.problems.join(' · ')}</span>
        </div>
      ) : null}

      {saveFailure === null ? null : (
        <CustomerFailureNotice failure={saveFailure} context="create" />
      )}

      <form
        className="cap-form"
        data-testid="customer-create-form"
        aria-label="เพิ่มลูกค้าใหม่"
        onSubmit={(event) => void onSubmit(event)}
        noValidate
      >
        <section className="cap-card cap-card--form" aria-label="ข้อมูลลูกค้า">
          <div className="cap-form__grid">
            <label className="cap-field">
              <span className="cap-field__label">
                รหัสลูกค้า
                <InfoHint
                  label="เกี่ยวกับรหัสลูกค้า"
                  text={
                    <>
                      ยาวไม่เกิน {CUSTOMER_CODE_MAX_LENGTH} ตัวอักษร ต้องไม่ซ้ำกันทั้งองค์กร และ{' '}
                      <strong>แก้ไม่ได้อีกเลยหลังสร้าง</strong> — ใบสั่งขายและการส่งของอ้างรหัสนี้
                    </>
                  }
                />
              </span>
              <input
                className="cap-input"
                name="customer_code"
                data-testid="customer-create-code"
                value={form.customer_code}
                maxLength={CUSTOMER_CODE_MAX_LENGTH}
                autoComplete="off"
                onChange={(event) => updateField('customer_code', event.target.value)}
              />
            </label>

            <label className="cap-field">
              <span className="cap-field__label">ชื่อลูกค้า</span>
              <input
                className="cap-input"
                name="customer_name"
                data-testid="customer-create-name"
                value={form.customer_name}
                maxLength={CUSTOMER_NAME_MAX_LENGTH}
                autoComplete="off"
                onChange={(event) => updateField('customer_name', event.target.value)}
              />
            </label>

            <label className="cap-field">
              <span className="cap-field__label">สถานะ</span>
              <select
                className="cap-input"
                name="status"
                data-testid="customer-create-status"
                value={form.status}
                onChange={(event) => updateField('status', event.target.value)}
              >
                {CUSTOMER_STATUSES.map((code) => (
                  <option key={code} value={code}>
                    {customerStatusLabel(code)}
                  </option>
                ))}
              </select>
            </label>

            <label className="cap-field">
              <span className="cap-field__label">ที่อยู่</span>
              <textarea
                className="cap-input"
                name="address"
                data-testid="customer-create-address"
                rows={4}
                value={form.address}
                onChange={(event) => updateField('address', event.target.value)}
              />
            </label>
          </div>
        </section>

        <CustomerContactFields
          testIdPrefix="customer-create"
          contacts={form.contacts}
          errors={contactErrors}
          onChange={updateContact}
        />

        <div className="cap-form__actions">
          <button
            type="submit"
            className="cap-button cap-button--primary"
            data-testid="customer-create-submit"
            disabled={submitDisabled}
          >
            {save.status === 'saving' ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </form>
    </div>
  );
}
